import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer, PropertyGrid,
  ListingSplit, ListingLeft, ListingRight,
  MapMobileFab, MapMobileOverlay, MapMobileHeader, MapMobileBody,
} from './styles/MFRealStateStyles';
import Navbar from './components/Navbar';
import FilterBar from './components/FilterBar';
import PropertyCard from './components/PropertyCard';
import PropertyMap from './components/PropertyMap';
import Footer from './components/Footer';
import { getPublishedProperties, getFilterOptions } from './services/propertyService';

const INITIAL_FILTERS = {
  busca: null,
  tipo_negocio: null,
  tipo: null,
  cidade: null,
  condominio: null,
  quartos_min: null,
  preco_min: null,
  preco_max: null,
};

const NUMERIC_FILTERS = new Set(['quartos_min', 'preco_min', 'preco_max']);

function filtersFromSearchParams(sp) {
  const out = { ...INITIAL_FILTERS };
  for (const key of Object.keys(INITIAL_FILTERS)) {
    const raw = sp.get(key);
    if (raw == null || raw === '') continue;
    out[key] = NUMERIC_FILTERS.has(key) ? Number(raw) : raw;
  }
  return out;
}

export default function PropertyListingPage() {
  const [searchParams] = useSearchParams();
  const initialFilters = filtersFromSearchParams(searchParams);
  const [properties, setProperties] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [debouncedFilters, setDebouncedFilters] = useState(initialFilters);
  const [filterOptions, setFilterOptions] = useState({ bairros: [], cidades: [], condominiosByCity: {} });
  const [highlightedId, setHighlightedId] = useState(null);
  const [mobileMapOpen, setMobileMapOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    getFilterOptions().then(setFilterOptions);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [filters]);

  const fetchProperties = useCallback(async () => {
    const data = await getPublishedProperties(
      Object.fromEntries(Object.entries(debouncedFilters).filter(([, v]) => v != null))
    );
    setProperties(data);
  }, [debouncedFilters]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // Bloqueia scroll do body quando overlay mobile esta aberto
  useEffect(() => {
    if (mobileMapOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [mobileMapOpen]);

  return (
    <PageContainer>
      <Navbar />

      <div style={{ paddingTop: 72 }}>
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          count={properties.length}
          cidades={filterOptions.cidades}
          condominiosByCity={filterOptions.condominiosByCity}
          tipos={filterOptions.tipos}
        />

        <ListingSplit>
          <ListingLeft>
            {properties.length > 0 ? (
              <PropertyGrid>
                {properties.map(property => (
                  <div
                    key={property.id}
                    onMouseEnter={() => setHighlightedId(property.id)}
                    onMouseLeave={() => setHighlightedId(null)}
                  >
                    <PropertyCard
                      property={property}
                      defaultTab={filters.tipo_negocio === 'locacao' ? 'aluguel' : 'venda'}
                    />
                  </div>
                ))}
              </PropertyGrid>
            ) : (
              <div style={{ textAlign: 'center', padding: '80px 0', color: '#A0A6B2' }}>
                <p style={{ fontSize: 18 }}>Nenhum imóvel encontrado com esses filtros.</p>
                <button
                  onClick={() => setFilters(INITIAL_FILTERS)}
                  style={{
                    marginTop: 16, background: 'none', border: '1px solid #E7E9EC',
                    padding: '10px 24px', borderRadius: 4, cursor: 'pointer', fontSize: 14,
                  }}
                >
                  Limpar filtros
                </button>
              </div>
            )}
          </ListingLeft>

          <ListingRight>
            <PropertyMap
              properties={properties}
              highlightedId={highlightedId}
              onMarkerHover={setHighlightedId}
            />
          </ListingRight>
        </ListingSplit>
      </div>

      {/* FAB mobile pra abrir o mapa fullscreen */}
      {properties.length > 0 && (
        <MapMobileFab onClick={() => setMobileMapOpen(true)}>
          <span aria-hidden="true">◉</span> Mapa
        </MapMobileFab>
      )}

      {mobileMapOpen && (
        <MapMobileOverlay>
          <MapMobileHeader>
            <h2>{properties.length} {properties.length === 1 ? 'imóvel' : 'imóveis'}</h2>
            <button type="button" onClick={() => setMobileMapOpen(false)}>
              ← Lista
            </button>
          </MapMobileHeader>
          <MapMobileBody>
            <PropertyMap properties={properties} />
          </MapMobileBody>
        </MapMobileOverlay>
      )}

      <Footer />
    </PageContainer>
  );
}
